import { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { TbLoader2 } from "react-icons/tb";
import DashboardLayout from "layout/DashboardLayout";
import PageTitle from "components/PageTitle";
import { ApiService } from "api/api.service";
import { useAppSelector } from "store";
import { useNavigate } from "react-router-dom";
import { RoleTypes } from "dtos/user.dto";

interface StageItem {
  type: string;
  count: number;
  y: number;
  height: number;
  rectWidth: number;
}

interface Stage {
  name: string;
  x: number;
  items: StageItem[];
  color: string;
}

export interface FlowConnection {
  from: string;
  to: string;
  value: number;
}

export interface FlowData {
  userTypeCounts: { type: string; count: number }[];
  appTypeCounts: { type: string; count: number }[];
  moduleCounts: { type: string; count: number }[];
  userToAppConnections: FlowConnection[];
  appToModuleConnections: FlowConnection[];
  totalUsers: number;
  totalApplications: number;
  totalModules: number;
}

const FlowVisualization = ({ height = 700 }: { height?: number }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  // const [isReady, setIsReady] = useState(false);
  const { data: queryData, isLoading } = ApiService.application.useGetJourneyStatistics();
  const data = queryData?.result;

  const user = useAppSelector((state) => state.user);
  const navigate = useNavigate();

  useEffect(() => {
    if (!Object.values(user).length) return;

    if (user.role !== RoleTypes.ADMIN && user.role !== RoleTypes.SUPERADMIN) {
      navigate("/dashboard");
    }
  }, [user]);

  useEffect(() => {
    if (!svgRef.current || isLoading || !data) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const renderFrame = requestAnimationFrame(() => {
      const svgElement = svgRef.current;
      if (!svgElement) return;

      const margin = { top: 60, right: 200, bottom: 40, left: 200 };
      const width = 1400;
      const innerWidth = width - margin.left - margin.right;
      const innerHeight = height - margin.top - margin.bottom;

      svg
        .attr("width", width)
        .attr("height", height)
        .attr("viewBox", `0 0 ${width} ${height}`)
        .attr("preserveAspectRatio", "xMidYMid meet");

      const container = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

      const calculateStageLayout = (items: { type: string; count: number }[], stageHeight: number) => {
        if (items.length === 0) return [];

        const itemHeight = 60;
        const minSpacing = 30;
        const totalItemsHeight = items.length * itemHeight;
        const totalSpacing = (items.length - 1) * minSpacing;
        const totalHeight = totalItemsHeight + totalSpacing;

        const startY = Math.max(0, (stageHeight - totalHeight) / 2);

        return items.map((item, index) => {
          const tempText = container
            .append("text")
            .attr("font-size", "12px")
            .attr("font-weight", "bold")
            .text(item.type.toUpperCase())
            .style("visibility", "hidden");

          const textWidth = tempText.node()?.getBBox().width || 0;
          tempText.remove();

          const padding = 32;
          const rectWidth = Math.max(textWidth + padding, 120);

          return {
            ...item,
            y: startY + index * (itemHeight + minSpacing),
            height: itemHeight,
            rectWidth,
          };
        });
      };

      const filteredModules = data.moduleCounts.filter((item: any) => item.count > 0);

      const stages: Stage[] = [
        {
          name: "Users",
          x: 0,
          items: calculateStageLayout(data.userTypeCounts, innerHeight),
          color: "#3b82f6",
        },
        {
          name: "Applications",
          x: innerWidth / 2,
          items: calculateStageLayout(data.appTypeCounts, innerHeight),
          color: "#f59e0b",
        },
        {
          name: "Modules",
          x: innerWidth,
          items: calculateStageLayout(filteredModules, innerHeight),
          color: "#ec4899",
        },
      ];

      stages.forEach((stage) => {
        container
          .append("text")
          .attr("x", stage.x)
          .attr("y", -20)
          .attr("text-anchor", "middle")
          .attr("fill", "#1f2937")
          .attr("font-size", "24px")
          .attr("font-weight", "bold")
          .attr("class", "stage-title")
          .text(stage.name);
      });

      stages.forEach((stage) => {
        stage.items.forEach((item) => {
          container
            .append("rect")
            .attr("x", stage.x - item.rectWidth / 2)
            .attr("y", item.y)
            .attr("width", item.rectWidth)
            .attr("height", item.height)
            .attr("fill", stage.color)
            .attr("stroke", "gray")
            .attr("stroke-width", 1)
            .attr("paint-order", "stroke")
            .attr("rx", 8)
            .style("opacity", 0.9)
            .style("cursor", "pointer")
            .style("transition", "all 0.2s ease");

          container
            .append("text")
            .attr("x", stage.x)
            .attr("y", item.y + item.height / 2 - 8)
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .attr("fill", "white")
            .attr("font-size", "12px")
            .attr("font-weight", "bold")
            .attr("class", "rect-text")
            .style("pointer-events", "none")
            .text(item.type.toUpperCase());

          container
            .append("text")
            .attr("x", stage.x)
            .attr("y", item.y + item.height / 2 + 12)
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .attr("fill", "white")
            .attr("font-size", "14px")
            .attr("font-weight", "bold")
            .attr("class", "rect-text")
            .style("pointer-events", "none")
            .text(`(${item.count.toLocaleString()})`);
        });
      });

      const drawCurvedPath = (
        x1: number,
        y1: number,
        height1: number,
        width1: number,
        x2: number,
        y2: number,
        height2: number,
        width2: number,
        value: number,
        maxValue: number,
        connection: FlowConnection,
      ) => {
        if (value === 0) return;

        const startX = x1 + width1 / 2;
        const endX = x2 - width2 / 2;
        const startY = y1 + height1 / 2;
        const endY = y2 + height2 / 2;

        const midX = (startX + endX) / 2;
        const thickness = Math.max(3, (value / maxValue) * 25);

        const gradientId = `gradient-${Math.random().toString(36).substr(2, 9)}`;

        const defs = container.select("defs").empty() ? container.append("defs") : container.select("defs");
        const gradient = defs.append("linearGradient").attr("id", gradientId).attr("x1", "0%").attr("x2", "100%");

        gradient.append("stop").attr("offset", "0%").attr("stop-color", "#10b981").attr("stop-opacity", 0.8);
        gradient.append("stop").attr("offset", "100%").attr("stop-color", "#06b6d4").attr("stop-opacity", 0.8);

        const path = `
          M ${startX} ${startY - thickness / 2}
          C ${midX} ${startY - thickness / 2}, ${midX} ${endY - thickness / 2}, ${endX} ${endY - thickness / 2}
          L ${endX} ${endY + thickness / 2}
          C ${midX} ${endY + thickness / 2}, ${midX} ${startY + thickness / 2}, ${startX} ${startY + thickness / 2}
          Z
        `;

        const pathElement = container
          .append("path")
          .attr("d", path)
          .attr("fill", `url(#${gradientId})`)
          .attr("class", "flow-path")
          .style("opacity", 0.8)
          .style("cursor", "pointer")
          .style("transition", "opacity 0.3s ease");

        pathElement
          .append("title")
          .text(`${connection.from} → ${connection.to}: ${value.toLocaleString()} connections`);

        pathElement
          .on("mouseover", function () {
            container.selectAll(".flow-path").style("opacity", 0.3);
            d3.select(this).style("opacity", 1);
          })
          .on("mouseout", function () {
            container.selectAll(".flow-path").style("opacity", 0.8);
          });
      };

      const userStage = stages[0];
      const appStage = stages[1];
      const moduleStage = stages[2];

      data.userToAppConnections.forEach((connection: FlowConnection) => {
        const userType = userStage.items.find((item) => item.type === connection.from);
        const appType = appStage.items.find((item) => item.type === connection.to);

        if (userType && appType && connection.value > 0) {
          drawCurvedPath(
            userStage.x,
            userType.y,
            userType.height,
            userType.rectWidth,
            appStage.x,
            appType.y,
            appType.height,
            appType.rectWidth,
            connection.value,
            Math.max(...data.userToAppConnections.map((c: FlowConnection) => c.value)),
            connection,
          );
        }
      });

      // Draw app to module connections
      data.appToModuleConnections.forEach((connection: FlowConnection) => {
        const appType = appStage.items.find((item) => item.type === connection.from);
        const moduleType = moduleStage.items.find((item) => item.type === connection.to);

        if (appType && moduleType && connection.value > 0) {
          drawCurvedPath(
            appStage.x,
            appType.y,
            appType.height,
            appType.rectWidth,
            moduleStage.x,
            moduleType.y,
            moduleType.height,
            moduleType.rectWidth,
            connection.value,
            Math.max(...data.appToModuleConnections.map((c: FlowConnection) => c.value)),
            connection,
          );
        }
      });
    });

    return () => cancelAnimationFrame(renderFrame);
  }, [height, data, isLoading]);

  return (
    <DashboardLayout>
      <PageTitle title="Journey" pageTitle={[{ title: "Journey" }]} />
      <div className="min-h-screen w-full overflow-x-scroll">
        <div className="min-w-[1200px] mx-auto">
          <div className="flex justify-center items-start">
            {isLoading || !data ? (
              <div className="flex flex-col items-center justify-center w-full h-64 space-y-4">
                <TbLoader2 className="animate-spin size-8" />
                <p className="text-gray-500">Loading journey visualization...</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <svg
                  ref={svgRef}
                  className="w-full h-auto transition-opacity duration-500 opacity-100"
                  style={{ minHeight: height }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FlowVisualization;
